import { Component, Injector, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, Attendance, Contact, SoftphoneService } from 'lib-trend-core';

/**
 * Inline "Ligar" trigger (contact info, kanban card, etc.). It only initiates the call — the live
 * call UI (modal + topbar pill) and the audio sink live in SoftphoneHostComponent.
 *
 * Accepts the contact to call either directly via [contact] or via [attendance] (uses its contact).
 */
@Component({
    selector: 'softphone-widget',
    templateUrl: './softphone-widget.component.html',
    styleUrls: ['./softphone-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SoftphoneWidgetComponent extends AbstractComponent implements OnInit {

  @Input() attendance: Attendance | null = null;
  @Input() contact: Contact | null = null;
  /** 'full' = pill with "Ligar" label; 'icon' = compact round icon-only button (tight layouts). */
  @Input() variant: 'full' | 'icon' = 'full';

  hasRamal = false;

  constructor(injector: Injector, public softphone: SoftphoneService) {
    super(injector);
  }

  ngOnInit(): void {
    // The button only appears when the company has the VoIP module enabled AND the
    // logged user has an active extension (ramal). Skip the channel lookup otherwise.
    if (super.getModuleTrendVoip()) {
      this.softphone.hasExtensionForUser(this.getIDCurrentUser())
        .then((has: boolean) => this.hasRamal = has)
        .catch(() => this.hasRamal = false);
    }
  }

  /** The contact to call — explicit [contact] takes precedence, otherwise the attendance's contact. */
  private get resolvedContact(): Contact | null {
    return this.contact ?? this.attendance?.contact ?? null;
  }

  get visible(): boolean {
    return !!this.phone && super.getModuleTrendVoip() && this.hasRamal;
  }

  get phone(): string {
    return this.resolvedContact?.phone || '';
  }

  get contactName(): string {
    return this.resolvedContact?.name || this.phone;
  }

  async call(): Promise<void> {
    // Guard against double-click / starting a second call while one is in progress.
    if (!this.phone || this.softphone.inProgress$.value) {
      return;
    }
    try {
      await this.softphone.call(
        { name: this.contactName, phone: this.phone, image: this.resolvedContact?.image },
        this.getIDCurrentUser(),
      );
    } catch {
      // Error is surfaced centrally via SoftphoneService.error$ (handled by SoftphoneHostComponent).
    }
  }
}
